import { useEffect, useMemo, useState } from "react";

import CuoButton from "@/app/admin/_common/CuoButton";
import { Input } from "@/app/admin/_common/ui/input";
import { Label } from "@/app/admin/_common/ui/label";
import { Textarea } from "@/app/admin/_common/ui/textarea";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/app/admin/_common/ui/select";
import type { Automation, AutomationSchema } from "@/app/_services/AutomationService";

import ActionConfigFields from "./ActionConfigFields";
import ConditionBuilder from "./ConditionBuilder";

const DAY = 86400;
const HOUR = 3600;
const MINUTE = 60;

function splitDelay(seconds: number) {
  if (seconds && seconds % DAY === 0) return { amount: seconds / DAY, unit: "days" };
  if (seconds && seconds % HOUR === 0) return { amount: seconds / HOUR, unit: "hours" };
  if (seconds && seconds % MINUTE === 0) return { amount: seconds / MINUTE, unit: "minutes" };
  return { amount: seconds ?? 0, unit: "seconds" };
}

const UNIT_SECONDS: Record<string, number> = {
  seconds: 1,
  minutes: MINUTE,
  hours: HOUR,
  days: DAY,
};

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-3 rounded-md border border-border bg-card p-4">
      <div className="flex flex-col gap-0.5">
        <h3 className="text-[13px] font-semibold text-foreground">{title}</h3>
        {description && (
          <p className="text-[12px] text-muted-foreground">{description}</p>
        )}
      </div>
      {children}
    </div>
  );
}

export default function SetupTab({
  automation,
  schema,
  onSave,
}: {
  automation: Automation;
  schema: AutomationSchema;
  onSave: (body: Partial<Automation>) => Promise<boolean>;
}) {
  const [draft, setDraft] = useState<Partial<Automation>>({});
  const [isSaving, setIsSaving] = useState(false);

  // Reset whenever the automation is reloaded, so a refetch after save does not leave
  // stale edits sitting in the form.
  useEffect(() => {
    setDraft({});
  }, [automation.id, automation.updated_at]);

  const value = <K extends keyof Automation>(key: K): Automation[K] =>
    (draft[key] !== undefined ? draft[key] : automation[key]) as Automation[K];

  const set = (patch: Partial<Automation>) => setDraft((d) => ({ ...d, ...patch }));

  const isDirty = Object.keys(draft).length > 0;

  const eventsByDomain = useMemo(() => {
    const grouped: Record<string, AutomationSchema["events"]> = {};
    for (const event of schema.events) {
      (grouped[event.domain] ??= []).push(event);
    }
    return grouped;
  }, [schema.events]);

  const eventKey = value("event_key");
  const actionType = value("action_type");

  // Which actions the server will accept for this event. Filtering here means the UI
  // cannot offer a combination the API would reject.
  const allowedActions = schema.event_action_compat[eventKey] ?? [];
  const actionSpec = schema.actions.find((a) => a.key === actionType);

  const delay = splitDelay(value("delay_seconds") ?? 0);
  const [delayUnit, setDelayUnit] = useState(delay.unit);

  async function handleSave() {
    setIsSaving(true);
    try {
      const ok = await onSave(draft);
      if (ok) setDraft({});
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div className="flex flex-col gap-4 pb-24">
      <Section
        title="Trigger"
        description="The event that starts this automation."
      >
        <Select
          value={eventKey}
          onValueChange={(next) => set({ event_key: next })}
          disabled={automation.is_system}
        >
          <SelectTrigger className="max-w-[420px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(eventsByDomain).map(([domain, events]) => (
              <SelectGroup key={domain}>
                <SelectLabel className="capitalize">{domain}</SelectLabel>
                {events.map((event) => (
                  <SelectItem
                    key={event.key}
                    value={event.key}
                    // Registered so the catalogue is visible, but nothing publishes it
                    // yet — offering it would create an automation that never fires.
                    disabled={!event.implemented}
                  >
                    {event.label}
                    {!event.implemented && " (coming soon)"}
                  </SelectItem>
                ))}
              </SelectGroup>
            ))}
          </SelectContent>
        </Select>
        {automation.is_system && (
          <p className="text-[11.5px] text-muted-foreground">
            This is a built-in automation, so its trigger cannot be changed — code
            elsewhere still depends on it.
          </p>
        )}
      </Section>

      <Section
        title="Conditions"
        description="Only run when these are true. Leave empty to run every time."
      >
        <ConditionBuilder
          schema={schema}
          eventKey={eventKey}
          value={value("conditions")}
          onChange={(conditions) => set({ conditions })}
        />
      </Section>

      <Section title="Delay" description="Wait before running. Leave at 0 to run immediately.">
        <div className="flex items-center gap-2">
          <Input
            type="number"
            min={0}
            value={delay.amount}
            className="w-[120px]"
            onChange={(e) =>
              set({
                delay_seconds: Math.max(0, Number(e.target.value || 0)) * UNIT_SECONDS[delayUnit],
              })
            }
            aria-label="Delay amount"
          />
          <Select
            value={delayUnit}
            onValueChange={(unit) => {
              setDelayUnit(unit);
              set({ delay_seconds: delay.amount * UNIT_SECONDS[unit] });
            }}
          >
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.keys(UNIT_SECONDS).map((unit) => (
                <SelectItem key={unit} value={unit}>
                  {unit}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <p className="text-[11.5px] text-muted-foreground">
          Delays are capped at 30 days, which is the longest a scheduled task can be
          deferred.
        </p>
      </Section>

      <Section title="Action" description="What this automation does when it runs.">
        <Select
          value={actionType}
          onValueChange={(next) => set({ action_type: next })}
        >
          <SelectTrigger className="max-w-[420px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {schema.actions
              .filter((a) => allowedActions.includes(a.key))
              .map((action) => (
                <SelectItem key={action.key} value={action.key}>
                  {action.label}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>

        {actionSpec && (
          <>
            <p className="text-[12px] text-muted-foreground">{actionSpec.description}</p>
            <div className="mt-1 border-t border-border pt-4">
              <ActionConfigFields
                fields={actionSpec.config_fields}
                templates={automation.templates ?? []}
                values={value("action_config") ?? {}}
                onChange={(name, fieldValue) =>
                  set({
                    action_config: {
                      ...(value("action_config") ?? {}),
                      [name]: fieldValue,
                    },
                  })
                }
              />
            </div>
          </>
        )}
      </Section>

      <Section title="Details">
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="automation-name" className="text-[12.5px]">
              Name
            </Label>
            <Input
              id="automation-name"
              value={value("name") ?? ""}
              className="max-w-[420px]"
              onChange={(e) => set({ name: e.target.value })}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="automation-description" className="text-[12.5px]">
              Description
            </Label>
            <Textarea
              id="automation-description"
              rows={2}
              value={value("description") ?? ""}
              onChange={(e) => set({ description: e.target.value })}
            />
          </div>
        </div>
      </Section>

      {isDirty && (
        <div className="sticky bottom-5 z-10 flex w-full items-center justify-between gap-3 rounded-md border border-border bg-card px-5 py-3">
          <span className="text-[12.5px] text-muted-foreground">
            You have unsaved changes
          </span>
          <div className="flex items-center gap-2">
            <CuoButton variant="outline" label="Discard" onClick={() => setDraft({})} />
            <CuoButton label="Save" disableOnAsync={isSaving} onClick={handleSave} />
          </div>
        </div>
      )}
    </div>
  );
}
